import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, Gift } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL, apiErr, KpiCard, cardStyle, sectionTitleStyle, thStyle, tdStyle } from '../shared';
import { fmtDateTime, fmtNum } from '../../../lib/fmt';
import { useConfirm } from '../../../components/admin/ConfirmDialog';

// ══════════════════════════════════════════════════════════════════════════════
// REFERIDOS tab — moderación de /api/admin/referrals
// ══════════════════════════════════════════════════════════════════════════════
const REFERRAL_STATUS_LABEL = {
  pending:         { label: 'Pendiente',        color: '#93c5fd' },
  flagged_same_ip: { label: 'Misma IP — revisar', color: '#f87171' },
  completed:       { label: 'Concedido',         color: '#34d399' },
  rejected:        { label: 'Rechazado',         color: 'rgba(224,247,255,0.4)' },
};

export default function ReferralsAdminTab() {
  const [rows, setRows]       = useState(null);
  const [error, setError]     = useState(null);       // fallo al cargar la lista
  const [actionMsg, setActionMsg] = useState(null);   // { ok, msg } de aprobar/rechazar
  const [busyId, setBusyId]   = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const load = () => {
    axios.get(`${BACKEND_URL}/api/admin/referrals`, { withCredentials: true })
      .then(({ data }) => { setRows(data); setError(null); })
      .catch((err)     => setError(apiErr(err, 'No se han podido cargar los referidos.')));
  };

  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    const r = rows.find((x) => x.id === id) || {};
    const ok = await confirm(action === 'approve' ? {
      title: 'Aprobar referido', tone: 'success', confirmLabel: 'Aprobar y dar cristales',
      body: <>
        <strong style={{ color: '#22d3ee' }}>{r.referrer_username}</strong> recibe <strong>{fmtNum(r.crystals_referrer)} ❄</strong> y{' '}
        <strong style={{ color: '#22d3ee' }}>{r.referred_username}</strong> recibe <strong>{fmtNum(r.crystals_referred)} ❄</strong>.
        {r.status === 'flagged_same_ip' && <><br /><span style={{ color: '#f87171', fontSize: 12 }}>Ojo: las dos cuentas comparten IP.</span></>}
      </>,
    } : {
      title: 'Rechazar referido', tone: 'danger', confirmLabel: 'Rechazar',
      body: <>No se darán cristales a <strong>{r.referrer_username}</strong> ni a <strong>{r.referred_username}</strong>.</>,
    });
    if (!ok) return;
    setBusyId(id);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/referrals/${id}/${action}`, {}, { withCredentials: true });
      setActionMsg({ ok: true, msg: action === 'approve' ? 'Referido aprobado y cristales concedidos.' : 'Referido rechazado.' });
      load();
    } catch (err) {
      setActionMsg({ ok: false, msg: apiErr(err, 'No se pudo procesar la acción.') });
    } finally {
      setBusyId(null);
    }
  };

  if (error && !rows) return <div className="register-error"><AlertCircle size={15} /><span>{error}</span></div>;
  if (!rows)  return <div style={{ padding: 24, color: 'rgba(224,247,255,0.55)' }}>Cargando referidos…</div>;

  const flaggedCount = rows.filter(r => r.status === 'flagged_same_ip').length;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {confirmDialog}
      {actionMsg && (
        <div className={`admin-feedback ${actionMsg.ok ? 'admin-feedback--ok' : 'admin-feedback--err'}`}>
          {actionMsg.ok ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
          <span>{actionMsg.msg}</span>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <KpiCard icon={<Gift size={18} />} label="Total referidos" value={rows.length.toLocaleString()} />
        <KpiCard icon={<CheckCircle size={18} />} label="Concedidos" value={rows.filter(r => r.status === 'completed').length} />
        <KpiCard icon={<AlertCircle size={18} />} label="A revisar (misma IP)" value={flaggedCount} subtitle={flaggedCount ? 'requieren aprobación manual' : undefined} />
      </div>

      <div style={cardStyle}>
        <h3 style={{ ...sectionTitleStyle, marginTop: 0 }}><Gift size={16} /> Referidos</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: 'rgba(224,247,255,0.55)', textAlign: 'left' }}>
                <th style={thStyle}>Fecha</th>
                <th style={thStyle}>Referrer</th>
                <th style={thStyle}>Referido</th>
                <th style={thStyle}>IP referrer</th>
                <th style={thStyle}>IP referido</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = REFERRAL_STATUS_LABEL[r.status] || { label: r.status, color: '#e0f7ff' };
                const actionable = r.status === 'pending' || r.status === 'flagged_same_ip';
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at)}</td>
                    <td style={{ ...tdStyle, color: '#22d3ee', fontWeight: 600 }}>
                      {r.referrer_username}
                      {!r.referrer_email_verified && <span style={{ color: '#fbbf24', fontSize: 10 }}> (email sin verificar)</span>}
                    </td>
                    <td style={tdStyle}>
                      {r.referred_username}
                      {!r.referred_email_verified && <span style={{ color: '#fbbf24', fontSize: 10 }}> (email sin verificar)</span>}
                    </td>
                    <td style={tdStyle}>{r.referrer_ip || '—'}</td>
                    <td style={tdStyle}>{r.referred_ip || '—'}</td>
                    <td style={{ ...tdStyle, color: st.color, fontWeight: 600 }}>{st.label}</td>
                    <td style={tdStyle}>
                      {actionable ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="cd-btn cd-btn--cyan"
                            style={{ padding: '4px 10px', fontSize: 11 }}
                            disabled={busyId === r.id}
                            onClick={() => act(r.id, 'approve')}
                          >
                            Aprobar
                          </button>
                          <button
                            className="cd-btn cd-btn--ghost"
                            style={{ padding: '4px 10px', fontSize: 11 }}
                            disabled={busyId === r.id}
                            onClick={() => act(r.id, 'reject')}
                          >
                            Rechazar
                          </button>
                        </div>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
